/* eslint-disable react/jsx-no-bind */
import { FC, useState, useEffect, useCallback, useMemo, useRef } from "react";
import cn from "classnames";
import { cva } from "class-variance-authority";
import Icon from "~/components/shared/Icon";
import { LIGHT_COLORS, DARK_COLORS } from "~/components/shared/NavatarPicker/index";

// useCarousel hook for color picker navigation
const useCarousel = (total: number) => {
  const [current, setCurrent] = useState(0);
  const [animationState, setAnimationState] = useState("idle");
  const [nextIndex, setNextIndex] = useState(0);

  const next = () => {
    if (animationState !== "idle") {
      return;
    }
    setNextIndex((current + 1) % total);
    setAnimationState("animating");
  };

  useEffect(() => {
    if (animationState === "animating" && nextIndex !== current) {
      const timer = setTimeout(() => {
        setCurrent(nextIndex);
        setNextIndex(0);
        setAnimationState("idle");
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [animationState, nextIndex, current]);

  return { current, next, animationState, nextIndex };
};

// Slide animation variants for color picker carousel
const slideVariants = cva(
  "transition-transform duration-300 ease-in-out absolute inset-0 h-full w-full",
  {
    variants: {
      state: {
        visible: "translate-x-0 z-[2]",
        hidden: "translate-x-full z-0 pointer-events-none !transition-none",
        "slide-out-left": "-translate-x-full z-[1]",
        "slide-in-right": "translate-x-0 z-[2]",
      },
    },
  },
);

export interface ColorPickerSectionProps {
  selectedColor: string | null | undefined;
  isCustomColor: boolean;
  onColorSelect: (color: string) => void;
  onEscape: () => void;
}

const ColorPickerSection: FC<ColorPickerSectionProps> = ({
  selectedColor,
  isCustomColor,
  onColorSelect,
  onEscape,
}) => {
  const { current, next, animationState, nextIndex } = useCarousel(3);
  const [customColorInput, setCustomColorInput] = useState("");
  const [isTypingHex, setIsTypingHex] = useState(false);

  // Convert 3-digit hex to 6-digit hex
  const expandHex = useCallback((hex: string): string => {
    if (/^#[0-9A-Fa-f]{3}$/.test(hex)) {
      const red = hex[1];
      const green = hex[2];
      const blue = hex[3];
      return `#${red}${red}${green}${green}${blue}${blue}`;
    }
    return hex;
  }, []);

  const dragColorRef = useRef(selectedColor);
  const lastUpdateRef = useRef(0);

  const handleColorPickerChange = useCallback(
    (newColor: string) => {
      dragColorRef.current = newColor;
      const now = Date.now();

      if (now - lastUpdateRef.current > 50) {
        // throttle to 50ms updates
        lastUpdateRef.current = now;
        onColorSelect(newColor);
      }
    },
    [onColorSelect],
  );

  const handleColorPickerFinish = useCallback(
    (finalColor: string) => {
      onColorSelect(finalColor);
      setCustomColorInput(finalColor);
    },
    [onColorSelect],
  );

  const debouncedSetColor = useMemo(() => {
    let timer: ReturnType<typeof setTimeout>;
    return (hex: string) => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        onColorSelect(expandHex(hex));
      }, 200);
    };
  }, [expandHex, onColorSelect]);

  const handleHexInputChange = useCallback(
    (newColor: string) => {
      setIsTypingHex(true);
      setCustomColorInput(newColor);

      if (/^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(newColor)) {
        debouncedSetColor(newColor);
      }

      setTimeout(() => setIsTypingHex(false), 300);
    },
    [debouncedSetColor],
  );

  // Sync custom hex input value when colour changes externally (but only if not typing)
  useEffect(() => {
    if (!isTypingHex) {
      setCustomColorInput(selectedColor || "#F5F5F5");
    }
  }, [selectedColor, isTypingHex]);

  const handleCustomColorSubmit = useCallback(() => {
    if (customColorInput && /^#[0-9A-Fa-f]{6}$/.test(customColorInput)) {
      onColorSelect(customColorInput);
    }
  }, [customColorInput, onColorSelect]);

  const getViewClassName = useCallback(
    (index: number) => {
      if (animationState === "idle") {
        return index === current
          ? slideVariants({ state: "visible" })
          : slideVariants({ state: "hidden" });
      }
      if (animationState === "animating") {
        if (index === current) {
          return slideVariants({ state: "slide-out-left" });
        }
        if (index === nextIndex) {
          return slideVariants({ state: "slide-in-right" });
        }
        return slideVariants({ state: "hidden" });
      }
      return slideVariants({ state: "hidden" });
    },
    [animationState, current, nextIndex],
  );

  const renderLightColors = () => (
    <div className="flex h-full items-center justify-between pl-4 pr-12">
      {LIGHT_COLORS.map((presetColor) => (
        <button
          key={presetColor}
          onClick={() => onColorSelect(presetColor)}
          className={cn(
            "h-6 w-6 scale-100 rounded-full border-2 border-white ring-2 ring-transparent transition-all hover:ring-gray-400 active:scale-90",
            selectedColor === presetColor
              ? "!ring-gray-600"
              : "ring-transparent hover:ring-gray-600",
          )}
          style={{ backgroundColor: presetColor }}
          title={presetColor}
          type="button"
          data-testid={`color-preset-${presetColor}`}
        />
      ))}
    </div>
  );

  const renderDarkColors = () => (
    <div className="flex h-full items-center justify-between pl-4 pr-12">
      {DARK_COLORS.map((presetColor) => (
        <button
          key={presetColor}
          onClick={() => onColorSelect(presetColor)}
          className={cn(
            "h-6 w-6 scale-100 rounded-full border-2 border-white ring-2 ring-transparent transition-all hover:ring-gray-400 active:scale-90",
            selectedColor === presetColor
              ? "!ring-gray-600"
              : "ring-transparent hover:ring-gray-600",
          )}
          style={{ backgroundColor: presetColor }}
          title={presetColor}
          type="button"
          data-testid={`color-preset-${presetColor}`}
        />
      ))}
    </div>
  );

  const renderCustomColorInput = () => (
    <div className="flex h-full items-center gap-1 pl-4 pr-12">
      <input
        type="color"
        value={selectedColor || "#F5F5F5"}
        onChange={(event) => handleColorPickerChange(event.target.value)}
        onMouseUp={(event) => handleColorPickerFinish((event.target as HTMLInputElement).value)}
        onBlur={(event) => handleColorPickerFinish((event.target as HTMLInputElement).value)}
        className={cn(
          "h-6 min-h-6 w-6 min-w-6 scale-100 cursor-pointer appearance-none rounded-full border-2 border-white transition-all active:scale-90 [&::-webkit-color-swatch-wrapper]:p-0 [&::-webkit-color-swatch]:border-0",
          isCustomColor
            ? "ring-2 ring-gray-400 hover:ring-gray-500"
            : "ring-2 ring-transparent hover:ring-gray-400",
        )}
        data-testid="custom-color-picker"
      />
      <span className="text-charcoal-400 ml-2 text-xs font-medium">HEX</span>
      <input
        type="text"
        name="custom-color-input"
        value={customColorInput}
        onChange={(event) => handleHexInputChange(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            onEscape();
          }
          if (event.key === "Enter") {
            handleCustomColorSubmit();
          }
        }}
        placeholder="#FFFFFF"
        className="ml-1 w-full rounded-sm border border-gray-100 bg-gray-100 px-2 py-0.5 text-sm text-black placeholder:text-gray-700 focus-within:!border-gray-600 hover:border-gray-400 focus:outline-none"
        pattern="^#[0-9A-Fa-f]{6}$"
        data-testid="custom-color-input"
      />
    </div>
  );

  return (
    <div className="relative flex h-14 w-[340px] overflow-hidden border-b border-gray-200 after:absolute after:right-0 after:z-10 after:h-12 after:w-12 after:bg-gradient-to-r after:from-transparent after:to-white after:to-70% after:content-['']">
      {/* Light Colors */}
      <div className={getViewClassName(0)}>{renderLightColors()}</div>
      {/* Dark Colors */}
      <div className={getViewClassName(1)}>{renderDarkColors()}</div>
      {/* Custom Colors */}
      <div className={getViewClassName(2)}>{renderCustomColorInput()}</div>
      <button
        onClick={next}
        className="btn btn-small btn-depth btn-flex dark-gray relative z-20 ml-auto mr-3 mt-2.5 h-6 w-6 items-center justify-center p-0 hover:text-black"
        data-testid="custom-color-button"
      >
        <Icon icon="arrow-right" className="h-3 w-3" />
      </button>
    </div>
  );
};

export default ColorPickerSection;
